import type { Plans } from "@/stores/plans";

/**
 * Regras centrais da tela única de assinatura (grid desktop + carrossel mobile).
 *
 * Centraliza TODA a lógica de habilitação/desabilitação de cada combinação
 * plano × período para que desktop e mobile compartilhem exatamente o mesmo
 * comportamento. As entradas são o estado do usuário (logado/ativo), o pedido
 * atual (`order`) e o saldo de upgrade; a saída é a descrição de cada célula.
 *
 * Sequência linear (não-downgrade), calculada no FRONT:
 *   Basic Mensal(1) < Basic Trim(2) < Basic Anual(3)
 *     < Plus Mensal(4) < Plus Trim(5) < Plus Anual(6)
 *       < Gold Anual(7)
 */

// Tipo de plano usado nas regras (nomes vindos do backend em `plan_type_name`).
export type PlanTypeName = "Basic" | "Plus" | "Gold";

// Período em meses (Mensal / Trimestral / Anual).
export type PlanMonths = 1 | 3 | 12;

// Estado do assinante Gold em relação à proximidade do vencimento.
export type GoldState = "none" | "gold-gt-30" | "gold-le-30";

// Estado do topo da tela (define qual título/subtítulo exibir).
export type TopState =
  | "guest"
  | "logged-not-active"
  | "active-upgrade"
  | "gold-gt-30"
  | "gold-le-30";

// Modo de renderização da célula (o COMPONENTE decide o markup a partir disto).
export type CellDisplayMode =
  | "price" // preço normal (visitante / logado não-ativo)
  | "upgrade" // "Use seu saldo" + upgrade_value
  | "balance-remainder" // "Use seu saldo" + (valor do plano − saldo), limitado a zero
  | "free" // code_share → GRÁTIS
  | "x"; // célula inexistente (Gold ≠ Anual)

// Motivo da decisão (útil para texto/depuração; não é exibido cru ao usuário).
export type CellReason =
  | "price"
  | "upgrade"
  | "balance-remainder"
  | "free"
  | "x"
  | "admin-locked" // Admin do site (level 1 ou 4): contratação bloqueada em todos os planos
  | "gold-locked" // Gold >30 dias: tudo travado
  | "gold-basic-blocked" // Gold <=30 dias: Basic permanece bloqueado
  | "gold-balance-covers" // Gold <=30 dias: saldo cobre o valor (não-restituível)
  | "balance-covers" // ativo não-Gold: saldo de upgrade cobre o valor (não-restituível)
  | "sequence-lower"; // ativo não-Gold: downgrade (abaixo da posição atual)

export interface PlanRuleCellDisplay {
  mode: CellDisplayMode;
  /** Valor mensal (quando aplicável ao layout de preço). */
  monthly?: number;
  /** Valor total/à vista (quando aplicável ao layout de preço). */
  total?: number;
  /** Valor único da célula (upgrade_value ou restante de saldo). */
  value?: number;
  /** Percentual de desconto (promo de visitante). */
  discountPct?: number;
  /** Indica "até 12x sem juros" (Gold). */
  showInstallments: boolean;
}

export interface PlanRuleCellResult {
  /** A célula existe na grade? (Gold ≠ Anual = inexistente.) */
  exists: boolean;
  /** A célula está habilitada para seleção? */
  enabled: boolean;
  /** Açúcar: célula existe E está habilitada. */
  selectable: boolean;
  /** Motivo da decisão. */
  reason: CellReason;
  /** O componente deve exibir "Use seu saldo"? */
  showUseBalance: boolean;
  /** Como o componente deve renderizar o valor da célula. */
  display: PlanRuleCellDisplay;
}

export interface UseAssinePlanRulesOptions {
  /** Pedido atual do usuário; pode ser `{}`/`false`/ausente até a API responder. */
  order?: MaybeRefOrGetter<any>;
  isLoggedIn?: MaybeRefOrGetter<boolean>;
  isActiveUser?: MaybeRefOrGetter<boolean>;
  /** Administrador do site (level 1 ou 4) — bloqueia contratação em todos os planos. */
  isAdminUser?: MaybeRefOrGetter<boolean>;
  /** Saldo de upgrade — STRING com vírgula (ex.: "123,45"). */
  saldoUpgrade?: MaybeRefOrGetter<string>;
}

export interface DescribeCellArgs {
  typeName: string;
  months: number;
  plan: Plans;
}

// Sequência linear: chave "TypeName-months" → posição (1..7).
const SEQUENCE: Array<{ typeName: PlanTypeName; months: PlanMonths }> = [
  { typeName: "Basic", months: 1 },
  { typeName: "Basic", months: 3 },
  { typeName: "Basic", months: 12 },
  { typeName: "Plus", months: 1 },
  { typeName: "Plus", months: 3 },
  { typeName: "Plus", months: 12 },
  { typeName: "Gold", months: 12 },
];

/** Posição (1..7) na sequência linear; 0 quando a combinação não existe. */
export function positionOf(typeName: string, months: number): number {
  const idx = SEQUENCE.findIndex(
    (s) => s.typeName === typeName && s.months === months
  );
  return idx === -1 ? 0 : idx + 1;
}

/** Converte o saldo (string com vírgula) para número. */
function parseSaldo(saldo: unknown): number {
  if (saldo == null) return 0;
  const n = Number(String(saldo).replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

/** Coage `plan.value` (string) para número. */
function planValueNumber(plan: Plans): number {
  const n = Number(plan?.value);
  return Number.isFinite(n) ? n : 0;
}

export function useAssinePlanRules(opts: UseAssinePlanRulesOptions = {}) {
  const storePlan = planStore();
  const storeUser = userStore();

  // Entradas reativas: usa o que foi passado em `opts` ou cai nas stores globais.
  const order = computed<any>(() => {
    const value = opts.order != null ? toValue(opts.order) : undefined;
    return value ?? {};
  });
  const isLoggedIn = computed<boolean>(() =>
    opts.isLoggedIn != null ? Boolean(toValue(opts.isLoggedIn)) : storeUser.isLoggedIn
  );
  const isActiveUser = computed<boolean>(() =>
    opts.isActiveUser != null
      ? Boolean(toValue(opts.isActiveUser))
      : storeUser.isActiveUser
  );
  const isAdminUser = computed<boolean>(() =>
    opts.isAdminUser != null
      ? Boolean(toValue(opts.isAdminUser))
      : storeUser.isAdminUser
  );
  const saldoUpgrade = computed<string>(() =>
    opts.saldoUpgrade != null
      ? String(toValue(opts.saldoUpgrade) ?? "")
      : storePlan.saldoUpgrade
  );

  const saldoNum = computed<number>(() => parseSaldo(saldoUpgrade.value));

  // Sequência linear exposta (cópia imutável para consumo externo).
  const sequence = computed(() => SEQUENCE.map((s) => ({ ...s })));

  // `order` ausente/`{}`/`false` = NÃO-ativo (não pisca o estado Gold).
  const hasOrder = computed<boolean>(() => {
    const o = order.value;
    return Boolean(o) && typeof o === "object" && Object.keys(o).length > 0;
  });

  // Posição atual do usuário na sequência (a partir de order.plan_type_name + order.period).
  const currentPosition = computed<number | null>(() => {
    if (!isActiveUser.value || !hasOrder.value) return null;
    const typeName = order.value?.plan_type_name;
    const months = Number(order.value?.period);
    if (!typeName || !months) return null;
    const pos = positionOf(typeName, months);
    return pos === 0 ? null : pos;
  });

  // Estado Gold via order.days_to_expire (>30 trava; <=30 libera).
  // Backend retorna o valor negativo (dias restantes calculados como now - finish);
  // usamos Math.abs para trabalhar sempre com a magnitude em dias.
  const goldState = computed<GoldState>(() => {
    if (!isActiveUser.value || !hasOrder.value) return "none";
    if (order.value?.plan_type_name !== "Gold") return "none";
    const raw = Number(order.value?.days_to_expire);
    if (!Number.isFinite(raw)) return "gold-gt-30"; // sem dado: trava por segurança
    const days = Math.abs(raw);
    return days > 30 ? "gold-gt-30" : "gold-le-30";
  });

  // Estado do topo (qual texto exibir).
  const topState = computed<TopState>(() => {
    if (!isLoggedIn.value) return "guest";
    if (!isActiveUser.value) return "logged-not-active";
    if (goldState.value === "gold-gt-30") return "gold-gt-30";
    if (goldState.value === "gold-le-30") return "gold-le-30";
    return "active-upgrade";
  });

  // ----- Construtores de célula -----

  function xCell(): PlanRuleCellResult {
    return {
      exists: false,
      enabled: false,
      selectable: false,
      reason: "x",
      showUseBalance: false,
      display: { mode: "x", showInstallments: false },
    };
  }

  function disabledCell(reason: CellReason): PlanRuleCellResult {
    return {
      exists: true,
      enabled: false,
      selectable: false,
      reason,
      showUseBalance: false,
      display: { mode: "x", showInstallments: false },
    };
  }

  // Célula de preço (visitante / logado não-ativo). Promo usa discount_value.
  function enabledCell(plan: Plans, typeName: string): PlanRuleCellResult {
    if (plan?.code_share === true) {
      return {
        exists: true,
        enabled: true,
        selectable: true,
        reason: "free",
        showUseBalance: false,
        display: { mode: "free", showInstallments: false },
      };
    }

    const value = planValueNumber(plan);
    const discount = Number(plan?.discount) || 0;
    const discountValue = Number(plan?.discount_value) || 0;
    const isPromo = discount > 0 && discountValue > 0 && discountValue !== value;
    const total = isPromo ? discountValue : value;
    const months = Number(plan?.months) || 1;
    const monthly = months > 0 ? total / months : total;

    return {
      exists: true,
      enabled: true,
      selectable: true,
      reason: "price",
      showUseBalance: false,
      display: {
        mode: "price",
        monthly,
        total,
        discountPct: isPromo ? discount : undefined,
        showInstallments: typeName === "Gold",
      },
    };
  }

  // Célula de upgrade/saldo (assinante ativo): "Use seu saldo" + valor.
  function upgradeOrBalanceCell(plan: Plans): PlanRuleCellResult {
    if (plan?.upgrade === true && plan?.upgrade_value != null) {
      const value = Number(plan.upgrade_value);
      return {
        exists: true,
        enabled: true,
        selectable: true,
        reason: "upgrade",
        showUseBalance: true,
        display: {
          mode: "upgrade",
          value: Number.isFinite(value) ? value : 0,
          showInstallments: false,
        },
      };
    }

    // Borda: habilitado pela sequência sem upgrade_value → restante = valor − saldo.
    const restante = Math.max(planValueNumber(plan) - saldoNum.value, 0);
    return {
      exists: true,
      enabled: true,
      selectable: true,
      reason: "balance-remainder",
      showUseBalance: true,
      display: {
        mode: "balance-remainder",
        value: restante,
        showInstallments: false,
      },
    };
  }

  /**
   * Descreve uma célula (plano × período) conforme a precedência do PRD.
   * Segue o pseudocódigo do plano técnico à risca.
   */
  function describeCell(args: DescribeCellArgs): PlanRuleCellResult {
    const { typeName, months, plan } = args;

    // 1) Gold em período ≠ Anual → célula inexistente ("X").
    if (typeName === "Gold" && months !== 12) {
      return xCell();
    }

    // 2) Admin do site (level 1 ou 4): contratação travada em qualquer plano/período.
    if (isAdminUser.value) {
      return disabledCell("admin-locked");
    }

    const pos = positionOf(typeName, months);

    // 3) Não-logado → preço (visitante).
    if (!isLoggedIn.value) {
      return enabledCell(plan, typeName);
    }

    // 4) Logado sem assinatura ativa → preço (comercialmente = visitante).
    if (isLoggedIn.value && !isActiveUser.value) {
      return enabledCell(plan, typeName);
    }

    // ----- ATIVO -----
    if (order.value?.plan_type_name === "Gold") {
      if (goldState.value === "gold-gt-30") {
        // Gold >30 dias: tudo travado.
        return disabledCell("gold-locked");
      }
      // Gold <=30 dias:
      if (typeName === "Basic") {
        return disabledCell("gold-basic-blocked");
      }
      if ((typeName === "Gold" && months === 12) || typeName === "Plus") {
        // Restrição de não-restituição: só libera se saldo < valor do plano.
        if (saldoNum.value >= planValueNumber(plan)) {
          return disabledCell("gold-balance-covers");
        }
        return upgradeOrBalanceCell(plan);
      }
      return disabledCell("gold-locked");
    }

    // Ativo não-Gold → sequência linear.
    const current = currentPosition.value;
    if (current != null && pos < current) {
      // Downgrade: abaixo da posição atual.
      return disabledCell("sequence-lower");
    }
    // Restrição de não-restituição (mesma regra do Gold <=30 dias): se o saldo de
    // upgrade já cobre o valor total do plano, o período fica bloqueado (cinza).
    if (saldoNum.value >= planValueNumber(plan)) {
      return disabledCell("balance-covers");
    }
    // Igual/acima da atual → habilita como upgrade/saldo.
    return upgradeOrBalanceCell(plan);
  }

  /** Açúcar para os rádios do mobile: a combinação está habilitada? */
  function isPeriodEnabled(typeName: string, months: number): boolean {
    const plan = findPlan(typeName, months);
    if (!plan) return false;
    return describeCell({ typeName, months, plan }).enabled;
  }

  /** Localiza o `plan` de uma combinação a partir de `plansType` da store. */
  function findPlan(typeName: string, months: number): Plans | undefined {
    const type = storePlan.plansType.find(
      (t) => t.plan_type_name === typeName
    );
    return type?.plans?.find((p) => Number(p.months) === Number(months));
  }

  return {
    sequence,
    currentPosition,
    goldState,
    topState,
    positionOf,
    describeCell,
    isPeriodEnabled,
    findPlan,
    // Expostos para depuração/uso eventual nos componentes.
    saldoUpgrade,
    saldoNum,
    isLoggedIn,
    isActiveUser,
    isAdminUser,
    order,
  };
}
